import { TableActionIconButton } from "_shared/_views/tables/table-toolbar";
// #region Imports
import { useMediaQuery } from "@mui/material";
import { Theme } from "@mui/material/styles";
import AddBoxIcon from "@mui/icons-material/AddBox";
import {
  MRT_TableInstance as TableInstance,
  type MRT_RowData as RowData,
} from "material-react-table";
import ListChips from "_shared/_views/list-pages/list-chips";
import type { ListChipCounts, ListChipSelection } from "_shared/_views/list-pages/list-chip-selection";
import {
  ListPageHeading,
  ListPageMobileSearch,
  ListPageMobileToolbar,
  ListPageTabs,
  type ListPageTab,
} from "_shared/_views/list-pages/list-page-heading";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import { useNavigate } from "react-router";
import useSessionUser from "permissions/session/use-session-user";
import { feeScheduleChipGroups } from "work/fee-schedule-templates/_views/pages/fee-schedule-list-chips";
import { PageTab } from "work/fee-schedule-templates/_views/pages/tabs";
// #endregion

// #region Props
type FeeScheduleToolbarProps<TData extends RowData> = {
  table: TableInstance<TData>;
  activeTab: PageTab;
  /** Each tab, with its count. Absent, the heading draws no tabs. */
  tabs?: readonly ListPageTab<PageTab>[];
  onTabChange?: (tab: PageTab) => void;
  /** The tab's chips: its Archived chip (#6716). Absent, none is on. */
  chips?: ListChipSelection;
  chipCounts?: ListChipCounts;
  onChipsChange?: (selection: ListChipSelection) => void;
};

/**
 * The fee schedules page's heading (#6712, #6716): the title, the Templates and In use tabs, each
 * tab's Archived chip, and on a narrow screen the create action, the search and the table tools.
 */
export default function FeeScheduleToolbar<TData extends RowData>(props: Readonly<FeeScheduleToolbarProps<TData>>) {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized element
  // drawn with it never re-renders: the column list kept the switches it first drew (#6599).
  "use no memo";

  const { table, activeTab, tabs, onTabChange, chips, chipCounts, onChipsChange } = props;

  // #region Hooks
  const navigate = useNavigate();
  const sessionUser = useSessionUser();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  // #region Render
  return (
    <ListPageHeading
      title="Fee schedules"
      titleAction={
        isMediumDisplaySize ? (
          // The same grant the desktop button asks for. The compact layout used to gate on identity
          // instead, so the button the desktop refused was one tap away on a phone (issue #4651).
          <TableActionIconButton
            aria-label="Create Template"
            color="primary"
            disabled={!sessionUser?.canCreateFeeScheduleTemplates()}
            onClick={() => navigate(`/fee-schedules/${PageTab.Templates}/create`)}
          >
            <AddBoxIcon fontSize="large" />
          </TableActionIconButton>
        ) : undefined
      }
      tabs={
        tabs ? (
          <ListPageTabs value={activeTab} tabs={tabs} onChange={(_event, tab) => onTabChange?.(tab)} />
        ) : undefined
      }
      chips={
        <ListChips
          groups={feeScheduleChipGroups}
          selection={chips ?? {}}
          counts={chipCounts}
          onChange={(selection) => onChipsChange?.(selection)}
        />
      }
      mobileToolbar={
        isMediumDisplaySize ? (
          <ListPageMobileToolbar>
            <ListPageMobileSearch table={table} />
            <TableToolsMenu table={table} />
          </ListPageMobileToolbar>
        ) : undefined
      }
    />
  );
}
